import {
  PairingType,
  RomanceTrope,
  CharacterPairing,
  ContentWarning,
  DarknessLevel,
  ROMANCE_TROPES,
  OVERLAP_CLUSTERS,
  DIRECT_MAPPINGS,
  COMPOUND_RULES,
} from '../data/romanceTaggingData'

export interface TaggingInputs {
  pairingType: PairingType | null
  tropes: string[]
  characterPairings: string[]
  contentWarnings: string[]
  darknessLevel: DarknessLevel | null
}

export interface ClassificationResult {
  relationship_dynamics: string[]
  aftertide_eligible: boolean
  aftertide_reasons: string[]
  overlay_tropes: string[]
  overlay_dynamics: string[]
  overlay_flags: {
    showDynamic: boolean
    showAfterTide: boolean
    showWarningBadge: boolean
  }
  darkness_required: boolean
}

export function computeRomanceClassification(
  inputs: TaggingInputs,
): ClassificationResult {
  const {
    pairingType,
    tropes,
    characterPairings,
    contentWarnings,
    darknessLevel,
  } = inputs
  const allTags = [...tropes, ...characterPairings, ...contentWarnings]

  let dynamics = new Set<string>()
  let aftertide = false
  let aftertideReasons = new Set<string>()
  let darknessRequired = false

  // 1. Evaluate Direct Mappings
  for (const tag of allTags) {
    const mapping = DIRECT_MAPPINGS.find((m) => m.tag === tag)
    if (mapping) {
      mapping.dynamics.forEach((d) => dynamics.add(d))
      if (mapping.aftertide === true) {
        aftertide = true
        aftertideReasons.add(`Triggered by: ${tag}`)
      }
    }

    // Check if tag is flagged (requires darkness)
    const tropeDef = ROMANCE_TROPES.find((t) => t.name === tag)
    if (tropeDef?.isFlagged) darknessRequired = true

    // All warnings flag darkness
    if (contentWarnings.includes(tag)) darknessRequired = true
  }

  // 2. Evaluate Compound Rules
  for (const rule of COMPOUND_RULES) {
    let match = true

    if (rule.ifHasAll) {
      for (const reqTag of rule.ifHasAll) {
        if (!allTags.includes(reqTag)) {
          match = false
          break
        }
      }
    }

    if (match && rule.ifHasAnyWarning !== undefined) {
      if (rule.ifHasAnyWarning && contentWarnings.length === 0) match = false
      if (!rule.ifHasAnyWarning && contentWarnings.length > 0) match = false
    }

    if (match && rule.ifDarkness) {
      if (!darknessLevel || !rule.ifDarkness.includes(darknessLevel)) {
        match = false
      }
    }

    if (match) {
      if (rule.thenDynamics) {
        rule.thenDynamics.forEach((d) => dynamics.add(d))
      }
      if (rule.thenAftertide) {
        aftertide = true
        const reasonStr = `Compound rule: ${rule.ifHasAll?.join(' + ') || ''}${
          rule.ifHasAnyWarning ? ' + Warnings' : ''
        }${rule.ifDarkness ? ` + Darkness (${rule.ifDarkness.join('/')})` : ''}`
        aftertideReasons.add(reasonStr)
      }
    }
  }

  // 3. Overlay Trope Selection
  let overlayTropes: string[] = []
  const selectedTropeDefs = tropes
    .map((t) => ROMANCE_TROPES.find((def) => def.name === t))
    .filter((t): t is RomanceTrope => t !== undefined)

  // Redundancy Filtering
  let filteredTropes = [...selectedTropeDefs]
  const clustersToCheck = Object.values(OVERLAP_CLUSTERS)

  for (const cluster of clustersToCheck) {
    const tropesInCluster = filteredTropes.filter((t) =>
      cluster.includes(t.name),
    )
    if (tropesInCluster.length > 1) {
      // Keep highest priority
      tropesInCluster.sort((a, b) => {
        if (a.isHighImpact && !b.isHighImpact) return -1
        if (!a.isHighImpact && b.isHighImpact) return 1
        return b.specificityScore - a.specificityScore
      })
      const toKeep = tropesInCluster[0]
      const toRemove = tropesInCluster.slice(1).map((t) => t.name)
      filteredTropes = filteredTropes.filter((t) => !toRemove.includes(t.name))
    }
  }

  // Sort remaining by priority
  filteredTropes.sort((a, b) => {
    if (a.isHighImpact && !b.isHighImpact) return -1
    if (!a.isHighImpact && b.isHighImpact) return 1
    return b.specificityScore - a.specificityScore
  })

  overlayTropes = filteredTropes.slice(0, 2).map((t) => t.name)

  // 4. Overlay Dynamics Selection
  // A dynamic is overlay-eligible if triggered by a high-impact trope, compound rule, or flagged warning.
  let overlayDynamics = new Set<string>()

  // Re-evaluate to find overlay-eligible dynamics
  for (const tag of allTags) {
    const tropeDef = ROMANCE_TROPES.find((t) => t.name === tag)
    const isHighImpact = tropeDef?.isHighImpact
    const isWarning = contentWarnings.includes(tag)

    if (isHighImpact || isWarning) {
      const mapping = DIRECT_MAPPINGS.find((m) => m.tag === tag)
      if (mapping) {
        mapping.dynamics.forEach((d) => overlayDynamics.add(d))
      }
    }
  }

  for (const rule of COMPOUND_RULES) {
    // If a compound rule matched and added dynamics, they are eligible
    // (We'd need to re-run the match logic or just assume if it's in `dynamics` and comes from a rule, it's eligible)
    // For simplicity, any dynamic added by a matched compound rule is eligible.
    let match = true
    if (rule.ifHasAll && !rule.ifHasAll.every((t) => allTags.includes(t)))
      match = false
    if (match && rule.ifHasAnyWarning && contentWarnings.length === 0)
      match = false
    if (
      match &&
      rule.ifDarkness &&
      (!darknessLevel || !rule.ifDarkness.includes(darknessLevel))
    )
      match = false

    if (match && rule.thenDynamics) {
      rule.thenDynamics.forEach((d) => overlayDynamics.add(d))
    }
  }

  return {
    relationship_dynamics: Array.from(dynamics),
    aftertide_eligible: aftertide,
    aftertide_reasons: Array.from(aftertideReasons),
    overlay_tropes: overlayTropes,
    overlay_dynamics: Array.from(overlayDynamics),
    overlay_flags: {
      showDynamic: overlayDynamics.size > 0,
      showAfterTide: aftertide,
      showWarningBadge:
        contentWarnings.length > 0 ||
        darknessLevel === 'Dark' ||
        darknessLevel === 'Pitch Black',
    },
    darkness_required: darknessRequired,
  }
}
